import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from './config';
import { Explain } from './Explain';
import EdgeSentence from './EdgeSentence';
import RelationField from './RelationField';
import ArrangeBar from './ArrangeBar';
import { relationColor, SpreadTick, TrustBand, LensChips, fmtScore } from './trustShared';
import { useLensWeights, recombine } from './lensWeights';
import {
  SORTS,
  DEFAULT_SORT,
  DEFAULT_HIDDEN_RELATIONS,
  arrangeEdges,
  relationOptions,
  farEndId,
} from './treeArrange';

// The relation tree (owner design session 2026-07-28; rulings in TODO.md
// "Question objects and the relation tree", rationale in
// records/RELATION_TREE_PREP.md). Replaces the flat L0 connections list and
// L1's grouped-edges fold: relations render as a collapsible tree under an
// object, and expanding a row shows what the far object is itself connected to.
//
// Three things that make this NOT a comment tree, all deliberate:
//
// 1. A row is the EDGE, not the far object. The relation is the thing being
//    voted on and the thing that carries meaning; the far object is its label.
// 2. ~~Ordering is NOT engagement.~~ **REVISED 2026-07-30 by owner ruling.**
//    This note used to read "Reddit orders by karma; that is the one thing this
//    surface must not borrow", and grouping was structural-first with activated
//    before inert. Both are gone: grouping is replaced by a filter plus a
//    selectable RANKING RULE (frontend/src/treeArrange.js), and `hot` is one of
//    the rules on offer. The owner's terms, verbatim: *"I want engagement as
//    long as it doesn't poison the trust graph."*
//
//    So the line moved, but it did not disappear — it moved from "no engagement
//    on this surface" to "engagement may ORDER, never SCORE, never DEFAULT, and
//    never HIDE". Those three conditions are enforced in treeArrange.js and
//    lib/engagement.ts and explained there. The epistemic-status ordering that
//    used to be implicit is now the `needs judgment` rule, which a viewer picks
//    rather than receives.
// 3. It is a GRAPH, not a tree. The same object is reachable by several paths
//    and cycles are legal. Both render in FULL — nothing is suppressed and
//    nothing is cut. A repeat carries a link back to where it already appears,
//    which is this tree's substitute for the line a graph rendering would have
//    drawn: a tree cannot draw edges across branches, so node identity has to
//    be re-encoded, and hypertext is the native way to do that.
//
// Walking a cycle is a FEATURE, not a bug to design around: "A supports B
// supports A" is a circular argument, and seeing it close the loop is a finding
// the project wants. Nothing runs away, because every level below the first is
// collapsed and expansion is a click — the recursion is user-driven and
// terminates whenever the reader stops.

// Per-branch render cap. A heavily-cited claim can carry hundreds of direct
// edges, which would blow up a branch regardless of cycles. Same pattern the
// old L0 list used (cap, then "show all"), applied per branch — and it lets a
// branch's size reveal itself progressively instead of via a number up front.
const BRANCH_LIMIT = 10;

// Prefetch exactly what is visible, and no more. Deliberately equal to
// BRANCH_LIMIT rather than its own number: prefetching past the fold fetches
// rows nobody has looked at, and "show all" on a 176-edge claim would then fire
// 176 requests.
const PREFETCH_LIMIT = BRANCH_LIMIT;

// The candidate-row draw gesture is the typed FIELD (owner ruling 2026-08-04,
// records/RELATION_DRAWING_PREP.md R4) — it replaced the three fixed verb
// buttons (supports/contradicts/related_to), which had a direction hardcoded
// as "that → verb → this" with no flip, and covered three names of an open
// namespace. The field says any relation, either direction, visibly.

const edgeDir = (e) =>
  (e.relation_name === 'supports' ? 1 : e.relation_name === 'contradicts' ? -1 : 0) * e.polarity;

// The far end of an edge, relative to the object whose row this is.
const farEnd = (edge, fromId) =>
  edge.source_id === fromId
    ? { id: edge.target_id, content: edge.target_content, type: edge.target_type }
    : { id: edge.source_id, content: edge.source_content, type: edge.source_type };

// Collapsed-branch summary. for/against counts only edges INTO the node that
// take an epistemic side, which is what makes it an epistemic reading rather
// than a row count. The raw count is deliberately NOT shown: it counts every
// touching edge while for/against counts only incoming epistemic ones, so the
// two answer different questions and showing both invites arithmetic that does
// not work (ruled 2026-07-28).
function summarize(edges, nodeId) {
  let forCount = 0;
  let againstCount = 0;
  for (const e of edges) {
    if (e.target_id !== nodeId) continue;
    const d = edgeDir(e);
    if (d > 0) forCount++;
    else if (d < 0) againstCount++;
  }
  return { forCount, againstCount };
}

// The spread half of the collapsed-row summary (the owner approved
// "for/against + spread" on 2026-07-28; for/against shipped that day and this
// did not, because it needs a lens score for every far end).
//
// Range of the viewer's BLEND across the objects on the far side of a collapsed
// branch: a wide tick means the things under here are judged very differently,
// a narrow one means they agree. Mirrors the Walk's foldSpread over its folds.
//
// Returns null — so SpreadTick renders nothing — when fewer than two far ends
// carry a score. A tick needs a range to mean anything, and one point drawn as
// a range reads as false precision. Unscored far ends (questions carry no truth
// value) are absent from `scores` by design and simply do not participate.
function spreadOf(edges, nodeId, scores, blendOf) {
  if (!scores) return null;
  const blends = [];
  for (const e of edges) {
    const farId = e.source_id === nodeId ? e.target_id : e.source_id;
    const s = scores[farId];
    if (s) blends.push(blendOf(s.byLens));
  }
  if (blends.length < 2) return null;
  return { min: Math.min(...blends), max: Math.max(...blends) };
}

const rowDomId = (pathKey) => `wvt-${pathKey.replace(/[^a-zA-Z0-9]/g, '')}`;

// Scroll an earlier occurrence into view and flash it, rather than navigating.
// Jumping away would cost the reader the context they just built — the whole
// point of the link is that it stays on the page.
function jumpTo(domId) {
  const el = document.getElementById(domId);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('wvt-flash');
  setTimeout(() => el.classList.remove('wvt-flash'), 1600);
}

// rowActions(edge, depth) → optional per-row controls. ObjectDetail passes
// delete for DEPTH 0 ONLY: those are the object's own edges, which is exactly
// what the L1 management list showed before the tree replaced it. Offering a
// destructive control at every depth would spray it across other objects'
// edges — worse than the surface it replaced.
// `candidates` are objects with NO edge to the root — the "not yet connected"
// group (owner call 2026-07-28). They arrive as a prop rather than being
// fetched here because ObjectDetail already has them: /breakdown returns
// `similar`, which is a full-text search excluding everything already related.
//
// Why they belong in the tree at all, given that principle 1 above says a row
// is the EDGE: a candidate row IS an edge — one that has not been drawn yet,
// whose verb is the buttons. Choosing a verb mints it, and the row migrates
// into its real relation group on refresh. That also extends the status ladder
// the tree already renders (activated before inert, because an unvoted edge is
// a suggestion rather than testimony) by exactly one rung: a candidate is not
// yet even proposed, so the group sorts LAST and starts collapsed.
//
// DEPTH 0 ONLY, and that is a hard rule rather than a simplification: a
// per-node candidate list would fire one full-text search per expansion, which
// is the per-request-recompute problem reappearing on a new surface.
export default function RelationTree({
  rootId,
  // Whether the ROOT is a question (2026-08-01, found by looking at the render).
  // Only the vote-mass subline reads it — see the guard at that line for why
  // suppressing beats rewording.
  rootIsQuestion = false,
  // the root's object_type — the matcher ranks and fixes direction by the
  // endpoint types (question layer), so the field needs to know what B is
  rootType,
  edges,
  user,
  onChanged,
  rowActions,
  candidates,
  siblingCandidates,
  lenses,
  compareIds,
  onToggleCompare,
}) {
  // The blend's weight subline (BLEND_REDESIGN_SPEC.md): each activated root
  // edge shows its share of the root's net activated vote mass — the
  // vote_consensus input, computed client-side from the prefetched edges so
  // no backend is involved. Root edges only: a deeper row's share would be
  // against a different base and would read as this one.
  const rootMass = edges
    .filter((e) => !e.is_system && e.up_votes + e.down_votes > 0)
    .reduce((a, e) => a + Math.abs(e.up_votes - e.down_votes), 0);

  // The viewset-shaped controls (owner ruling 2026-07-30): which relations show,
  // and which ranking rule orders them. One setting for the whole tree rather
  // than per level — it is a view of the graph, and a per-level control would
  // mean the same question is answered differently three rows apart.
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [hiddenRelations, setHiddenRelations] = useState(
    () => new Set(DEFAULT_HIDDEN_RELATIONS)
  );
  const toggleRelation = (relation) =>
    setHiddenRelations((prev) => {
      const next = new Set(prev);
      if (next.has(relation)) next.delete(relation);
      else next.add(relation);
      return next;
    });

  // fetched edges per object id — one entry per expanded far object
  const [cache, setCache] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(() => new Set());
  const [shownMore, setShownMore] = useState(() => new Set());
  // Candidates start CLOSED — the least-certain material on the page must not
  // compete with edges someone actually drew.
  // Which candidate groups are open, keyed by group name. (This used to mirror
  // a `closedGroups` set for the relation groups; those groups are gone as of
  // 2026-07-30, so this is now the only collapse state of its kind.)
  const [openCandGroups, setOpenCandGroups] = useState(() => new Set());
  const toggleCandGroup = (key) =>
    setOpenCandGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // which candidate row has its relation field open (one at a time — the
  // field is the gesture, not a per-row form that stays around)
  const [connecting, setConnecting] = useState(null);
  // Per-row trust disclosure, keyed by path. Closed by default: the tree is a
  // map of relations, and an always-on score per row would turn every branch
  // into a leaderboard, which is the one thing this surface must not borrow.
  const [trustOpen, setTrustOpen] = useState(() => new Set());
  const inflight = useRef(new Set());

  // Lens scores for far-end objects, for the collapsed-row spread tick.
  // object id → { byLens, conflict }. Fetched from the BATCHED endpoint: one
  // request for every id the tree currently knows about, not one per row.
  // Calling /breakdown per child would rebuild the whole graph and re-run six
  // engines once per child — the per-request-recompute problem in miniature,
  // which is precisely why this shipped as an endpoint rather than a loop.
  const [scores, setScores] = useState({});
  const scoresAsked = useRef(new Set());
  const { weights } = useLensWeights();
  // Blending is the viewer's, so it stays client-side: the server returns
  // per-lens scores and never a combined number. Axes are deliberately not
  // mixed in here — they sit at weight zero by default, and a tick is too small
  // a mark to carry a reading the viewer has not opted into.
  const blendOf = useCallback((byLens) => recombine(byLens || {}, weights), [weights]);

  // Every object that appears as the far end of any edge the tree has loaded.
  // Recomputed as branches open; the effect below fetches only what is new.
  const knownFarEnds = useMemo(() => {
    const ids = new Set();
    const collect = (list, fromId) => {
      for (const e of list || []) {
        if (e.is_system) continue;
        ids.add(e.source_id === fromId ? e.target_id : e.source_id);
      }
    };
    collect(edges, rootId);
    for (const [objectId, list] of Object.entries(cache)) collect(list, objectId);
    // Candidates ride the SAME batch — they are objects with scores like any
    // other, and the endpoint was built to take a set. No extra request. Both
    // groups go in together for that reason; a second batch per group would
    // reintroduce the per-row request the batched endpoint exists to avoid.
    for (const c of candidates || []) ids.add(c.id);
    for (const c of siblingCandidates || []) ids.add(c.id);
    return ids;
  }, [edges, cache, rootId, candidates, siblingCandidates]);

  // Unmount guard only — deliberately NOT a per-run cancellation.
  //
  // This was a per-effect `cancelled` flag first, and driving the render caught
  // it: expanding several rows in a row re-ran the effect, whose cleanup
  // cancelled the PREVIOUS fetch, while its ids had already been marked asked —
  // so those scores were dropped and never retried. Six branches qualified for
  // a tick and two rendered. A merge keyed by object id can never be made wrong
  // by a late response, so there is nothing for cancellation to protect here.
  // The flag is re-armed in the effect BODY, not just initialised. index.js
  // renders under React.StrictMode, which in development mounts, unmounts and
  // remounts every component: a cleanup-only guard latches false on that first
  // synthetic unmount and never comes back, so every response is discarded and
  // the tick never renders anywhere. Caught by driving the render — it is
  // invisible in production builds, which is what makes it worth a comment.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const missing = [...knownFarEnds].filter((id) => !scoresAsked.current.has(id));
    if (!missing.length) return;
    missing.forEach((id) => scoresAsked.current.add(id));
    const release = () => missing.forEach((id) => scoresAsked.current.delete(id));
    (async () => {
      try {
        // The endpoint caps at 200 ids; chunk so a very wide branch degrades
        // into a few requests instead of one 400.
        for (let i = 0; i < missing.length; i += 200) {
          const chunk = missing.slice(i, i + 200);
          const res = await apiFetch(`/api/claims/scores?ids=${chunk.join(',')}`);
          if (!res.ok) {
            release(); // let a later expansion retry rather than burning the ids
            return;
          }
          const body = await res.json();
          if (!mounted.current) return;
          // `unscored` ids are intentionally NOT recorded — an absent entry is
          // how spreadOf skips them, which keeps "no truth value" rendering as
          // absence rather than as a score of zero.
          setScores((s) => ({ ...s, ...body.scores }));
        }
      } catch {
        // The tick is an enhancement, so a failure stays silent — but the ids go
        // back in the pool so it is not silent AND permanent.
        release();
      }
    })();
  }, [knownFarEnds]);

  // ENGAGEMENT, and only if the viewer asked for it.
  //
  // Fetched lazily on the `hot` sort rather than alongside the scores, and that
  // is a property worth keeping: choose any other ranking rule and the
  // engagement numbers are never requested at all. It also keeps karma in a
  // separate object from the lens scores — see lib/engagement.ts for why
  // `scores[id].hot` must not be a thing that exists.
  const [engagement, setEngagement] = useState({});
  const engagementAsked = useRef(new Set());
  useEffect(() => {
    if (sort !== 'hot') return;
    const missing = [...knownFarEnds].filter((id) => !engagementAsked.current.has(id));
    if (!missing.length) return;
    missing.forEach((id) => engagementAsked.current.add(id));
    const release = () => missing.forEach((id) => engagementAsked.current.delete(id));
    (async () => {
      try {
        for (let i = 0; i < missing.length; i += 200) {
          const chunk = missing.slice(i, i + 200);
          const res = await apiFetch(`/api/engagement?ids=${chunk.join(',')}`);
          if (!res.ok) {
            release();
            return;
          }
          const body = await res.json();
          if (!mounted.current) return;
          setEngagement((s) => ({ ...s, ...body.engagement }));
        }
      } catch {
        release();
      }
    })();
  }, [sort, knownFarEnds]);

  const fetchEdges = useCallback(async (objectId) => {
    if (inflight.current.has(objectId)) return;
    inflight.current.add(objectId);
    setLoading((s) => new Set(s).add(objectId));
    try {
      const res = await apiFetch(`/api/relation-instances/${objectId}`);
      const rows = res.ok ? await res.json() : [];
      setCache((c) => ({ ...c, [objectId]: rows }));
    } catch {
      setCache((c) => ({ ...c, [objectId]: [] }));
    } finally {
      inflight.current.delete(objectId);
      setLoading((s) => {
        const n = new Set(s);
        n.delete(objectId);
        return n;
      });
    }
  }, []);

  // FIRST-LEVEL PREFETCH (owner call, built 2026-07-30 — TODO had it as the
  // tree's biggest owed item since 2026-07-28).
  //
  // The problem it fixes: a collapsed row summarises what has been FETCHED, and
  // lazy-fetch-on-expand meant that was nothing until you had already opened the
  // row. So "3 for · 1 against" was a memory aid, not a preview — it appeared
  // only for branches you had already read, which is precisely when you no
  // longer need it.
  //
  // Why it was not built until now: pulling a row's edges also pulls its
  // children into `knownFarEnds`, which fires the batched score request for all
  // of them. That request cost ~1s before the shape-③ cache landed earlier
  // today and costs ~40ms after, which turns "one second of jank per visible
  // row" into something a reader never notices. The dependency was real, not
  // bookkeeping.
  //
  // Depth 0 only, capped at what is visible. Deeper levels stay lazy: a reader
  // who has expanded three levels has told you what they are interested in, and
  // prefetching every branch of a graph with cycles in it has no natural end.
  useEffect(() => {
    const visible = arrangeEdges(edges || [], rootId, {
      sort,
      hiddenRelations,
      scores,
      engagement,
      blendOf,
    }).slice(0, PREFETCH_LIMIT);
    for (const edge of visible) {
      const id = farEndId(edge, rootId);
      if (!cache[id] && !inflight.current.has(id)) fetchEdges(id);
    }
  }, [edges, rootId, sort, hiddenRelations, scores, engagement, blendOf, cache, fetchEdges]);

  const toggleTrust = (pathKey) =>
    setTrustOpen((s) => {
      const n = new Set(s);
      if (n.has(pathKey)) n.delete(pathKey);
      else n.add(pathKey);
      return n;
    });

  const toggleRow = (pathKey, objectId) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(pathKey)) n.delete(pathKey);
      else {
        n.add(pathKey);
        if (!cache[objectId]) fetchEdges(objectId);
      }
      return n;
    });
  };

  // `seen` is populated during the render walk: object id → the DOM id of the
  // FIRST row that showed it. A later row for the same object is a repeat and
  // links back. Safe to mutate during render because the walk is synchronous
  // and its order is fully determined by the state above.
  const seen = new Map();

  // The root's edges arrive as a prop (ObjectDetail already fetched them);
  // every deeper level comes from the lazy cache. One accessor so the walk
  // reads a single structure without writing to state during render.
  const edgesFor = (objectId) => (objectId === rootId ? edges || [] : cache[objectId] || []);

  const renderLevel = (objectId, path, depth) => {
    const all = edgesFor(objectId);
    const arranged = arrangeEdges(all, objectId, {
      sort,
      hiddenRelations,
      scores,
      engagement,
      blendOf,
    });

    if (!arranged.length) {
      // A filter may hide rows — that is what a viewer asked it to do — but it
      // must SAY SO. Silent emptiness reads as "nothing connects to this", which
      // is a claim about the graph rather than about the current view.
      const hiddenHere = all.filter(
        (e) => !e.is_system && hiddenRelations.has(e.relation_name)
      ).length;
      return (
        <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', padding: 'var(--wv-sp-0) 0 var(--wv-sp-2) var(--wv-sp-3)' }}>
          {hiddenHere
            ? `${hiddenHere} connection${hiddenHere === 1 ? '' : 's'} hidden by your filter`
            : 'nothing further connects to this'}
        </div>
      );
    }

    // The cap is now per LEVEL rather than per group — there are no groups to
    // cap. Same progressive-reveal idiom: show ten, then say how many more.
    const levelKey = `${path}/lvl`;
    const cap = shownMore.has(levelKey) ? arranged.length : BRANCH_LIMIT;
    const visible = arranged.slice(0, cap);

    return (
      <div>
        {visible.map((edge) => renderRow(edge, objectId, path, depth))}
        {arranged.length > cap && (
          <button
            className="wvt-more"
            onClick={() => setShownMore((s) => new Set(s).add(levelKey))}
          >
            show {arranged.length - cap} more
          </button>
        )}
      </div>
    );
  };

  const renderRow = (edge, objectId, path, depth) => {
    const far = farEnd(edge, objectId);
    const pathKey = `${path}/${edge.id}`;
    const domId = rowDomId(pathKey);
    const isOpen = expanded.has(pathKey);
    const isLoading = loading.has(far.id) && isOpen;

    // Repeat detection. `seen` covers BOTH cases with one mechanism: a loop
    // (the object is among its own ancestors) and a diamond (the same object
    // reached down a different branch). Neither is suppressed — the row renders
    // in full either way, and the link is an annotation, not a restriction.
    const firstDom = seen.get(far.id);
    const isRepeat = Boolean(firstDom);
    if (!isRepeat) seen.set(far.id, domId);

    // RESOLVED 2026-07-30 by the first-level prefetch (see the effect above).
    // This note used to read: "a collapsed row can only summarize what has been
    // fetched, and lazy-fetch-on-expand means that is nothing until you have
    // opened it once, so the summary reads as a memory aid, not a preview."
    // Rows visible at depth 0 now have their children fetched before you click,
    // so for/against and the spread tick are genuine previews there. Below depth
    // 0 the old caveat still holds and the summary stays absent until expansion
    // — deliberately, because prefetching every branch of a graph with cycles
    // has no natural stopping point.
    const childEdges = cache[far.id];
    const visibleChildren =
      isOpen || !childEdges ? null : childEdges.filter((e) => e.id !== edge.id && !e.is_system);
    const summary = visibleChildren ? summarize(visibleChildren, far.id) : null;
    const spread = visibleChildren ? spreadOf(visibleChildren, far.id, scores, blendOf) : null;
    // Absent for an unscored far end (a question carries no truth value), and
    // absent is how it must render — never a 0.
    const farScore = scores[far.id];
    const showTrust = trustOpen.has(pathKey);

    return (
      <div key={pathKey} id={domId} className="wvt-row">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--wv-sp-1)' }}>
          <button
            className="wvt-caret"
            onClick={() => toggleRow(pathKey, far.id)}
            aria-expanded={isOpen}
            title={isOpen ? 'collapse' : 'what connects to this'}
          >
            <span className={`wv-chevron${isOpen ? ' down' : ''}`} aria-hidden="true" />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <EdgeSentence
              edge={edge}
              focusId={objectId}
              user={user}
              onVoted={onChanged}
              onRetyped={onChanged}
              actions={rowActions ? rowActions(edge, depth) : null}
            />
            {isRepeat && (
              <button className="wvt-repeat" onClick={() => jumpTo(firstDom)}>
                ↩ you were here
              </button>
            )}
            {/* SUPPRESSED on a question root (2026-08-01, found by looking at
                the render on a real question page, where it read "…% of this
                claim's vote mass" about a question). Rewording it to "object"
                would have hidden the real fault: the share is of the
                vote_consensus INPUT, and a question is not truth-apt, so no
                vote_consensus is ever computed for it. The percentage would be
                a real number describing a score that does not exist — which
                renders exactly like one that does. Absence, not a relabel. */}
            {depth === 0 && !rootIsQuestion && !edge.is_system && edge.up_votes + edge.down_votes > 0 && rootMass > 0 && (
              <div className="wv-data" style={{ fontSize: 'var(--wv-fs-micro)', color: 'var(--wv-quiet)', marginTop: 'var(--wv-sp-0)' }}>
                net {edge.up_votes - edge.down_votes >= 0 ? '+' : ''}{edge.up_votes - edge.down_votes}
                {' · '}{Math.round((Math.abs(edge.up_votes - edge.down_votes) / rootMass) * 100)}% of this claim's vote mass
              </div>
            )}
            {/* the net count alone still means something on a question — it is
                a vote tally, not a share of anything uncomputed */}
            {depth === 0 && rootIsQuestion && !edge.is_system && edge.up_votes + edge.down_votes > 0 && (
              <div className="wv-data" style={{ fontSize: 'var(--wv-fs-micro)', color: 'var(--wv-quiet)', marginTop: 'var(--wv-sp-0)' }}>
                net {edge.up_votes - edge.down_votes >= 0 ? '+' : ''}{edge.up_votes - edge.down_votes}
              </div>
            )}
            {summary && (summary.forCount > 0 || summary.againstCount > 0) && (
              <span className="trust-muted wvt-summary">
                {summary.forCount} for · {summary.againstCount} against
              </span>
            )}
            {spread && (
              <Explain concept="trust-band">
                <span
                  className="wvt-summary"
                  title={`what's under here spans ${spread.min.toFixed(2)} to ${spread.max.toFixed(2)} under your weights`}
                >
                  <SpreadTick spread={spread} />
                </span>
              </Explain>
            )}
            {/* The far OBJECT's own trust readout, which is what the deleted
                "From the same source" cards carried. It hangs off the object,
                not off the edge: the toggle's label is the object's blend, and
                opening it gives the band + per-lens chips. Keeping it distinct
                from the edge's own vote controls is the whole reason it renders
                here rather than inside EdgeSentence. */}
            {farScore && (
              <>
                <button
                  className="wvt-trust"
                  onClick={() => toggleTrust(pathKey)}
                  title={`trust readout for “${(far.content || '').slice(0, 60)}”`}
                  aria-expanded={showTrust}
                >
                  {fmtScore(blendOf(farScore.byLens))} <span className={`wv-chevron${showTrust ? ' down' : ''}`} aria-hidden="true" />
                </button>
                {showTrust && (
                  <div className="wvt-trustpanel">
                    <Explain concept="trust-band">
                      <TrustBand
                        scores={farScore.byLens}
                        blend={blendOf(farScore.byLens)}
                        width={200}
                      />
                    </Explain>
                    <div style={{ marginTop: 'var(--wv-sp-1)' }}>
                      <LensChips lenses={lenses || []} scores={farScore.byLens} size="small" />
                    </div>
                    {/* Compare lived on the deleted NeighborRow cards, and the
                        compare BAR at the bottom of the object page had no other
                        way to be fed — deleting the cards without this would
                        have quietly stranded a shipped surface. It belongs in
                        the disclosure rather than the row line for the same
                        reason the score does: it is an action on the OBJECT,
                        while the row line is about the edge. */}
                    {onToggleCompare && (
                      <button
                        onClick={() => onToggleCompare(far.id)}
                        style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-2)' }}
                      >
                        {(compareIds || []).includes(far.id) ? '✓ comparing' : '+ compare'}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {isOpen && (
          <div className="wvt-children">
            {isLoading ? (
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', padding: 'var(--wv-sp-1) 0 var(--wv-sp-1) var(--wv-sp-3)' }}>
                loading…
              </div>
            ) : (
              renderLevel(far.id, pathKey, depth + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  // The candidate groups. Named for WHAT these are, not for how they were found
  // — "similar" described the retrieval, which is the reader's least interesting
  // fact about them.
  //
  // TWO groups since 2026-07-30, and the second is the reason the first was not
  // enough. Measured by `scripts/retrieval_probe.js`: **531 unconnected
  // near-duplicate claim pairs from the SAME ARTICLE exist on dev, retrieval can
  // find 98.5% of them, and this surface showed 0%** — because the backend puts
  // same-article claims in `sameSource` (rendered nowhere) and excludes them from
  // the cross-page `similar` query. `records/F4_RETRIEVAL_DIAGNOSIS.md` had
  // already proved intra-article contradictions are invisible at decompose time
  // for every page, always, so this was the one surface that could repair them by
  // hand and it left them out.
  //
  // They stay SEPARATE rather than merging into one ranked list: "another claim
  // from this article" and "a claim from somewhere else" are different enough
  // that a reader wants to know which they are looking at, and the cross-page
  // ones are the cross-source contest the project values most. Merging would
  // also drown them — a claim has a median of 96 same-article siblings.
  const renderCandidateGroup = ({ key, list, label, blurb }) => {
    if (!list || !list.length) return null;
    const open = openCandGroups.has(key);
    return (
      <div style={{ marginTop: 'var(--wv-sp-4)' }} className="wvt-cand">
        <button
          onClick={() => toggleCandGroup(key)}
          className="wvt-group"
          style={{ color: 'var(--wv-muted)' }}
          aria-expanded={open}
        >
          <span className={`wv-chevron${open ? ' down' : ''}`} aria-hidden="true" style={{ opacity: 0.7 }} /> {label}
          <span className="trust-muted" style={{ fontWeight: 'normal', marginLeft: 'var(--wv-sp-2)' }}>
            {list.length}
          </span>
        </button>
        {open && (
          <div>
            {/* THE COUNT DOES NOT GO DOWN WHEN YOU LINK ONE, AND THAT IS
                CORRECT — verified by round trip 2026-07-28. `similar` is a
                TOP-8 query that excludes everything already related, so linking
                a candidate drops it and promotes the 9th; the group stays at 8
                with a different last row. It looks like a bug and is not one.
                (Confirmed the linked object is genuinely excluded, rather than
                merely re-sorted, before writing this.) */}
            <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', margin: 'var(--wv-sp-0) 0 var(--wv-sp-2) var(--wv-sp-3)' }}>
              {blurb}
              {user
                ? ' Connect one by saying the relation — phrase it either way round; the direction follows the words.'
                : ' Log in to connect one.'}
            </p>
            {list.map((node) => {
              const sc = scores[node.id];
              return (
                <div key={node.id} className="wvt-row wvt-candrow">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link to={`/object/${node.id}`} className="wvt-candtext">
                      {node.content}
                    </Link>
                    <div className="wvt-candactions">
                      {sc && (
                        <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
                          {fmtScore(blendOf(sc.byLens))}
                        </span>
                      )}
                      {user && (
                        <button
                          onClick={() => setConnecting(connecting === node.id ? null : node.id)}
                          aria-expanded={connecting === node.id}
                          style={{ fontSize: 'var(--wv-fs-xs)' }}
                        >
                          {connecting === node.id ? 'cancel' : '+ connect'}
                        </button>
                      )}
                      {onToggleCompare && (
                        <button onClick={() => onToggleCompare(node.id)} style={{ fontSize: 'var(--wv-fs-xs)' }}>
                          {(compareIds || []).includes(node.id) ? '✓ comparing' : '+ compare'}
                        </button>
                      )}
                    </div>
                    {connecting === node.id && (
                      <div style={{ marginTop: 'var(--wv-sp-2)' }}>
                        <RelationField
                          a={{ id: node.id, type: node.object_type || 'claim' }}
                          b={{ id: rootId, type: rootType }}
                          aLabel="that"
                          bLabel="this"
                          user={user}
                          autoFocus
                          onCancel={() => setConnecting(null)}
                          onDrawn={() => {
                            setConnecting(null);
                            if (onChanged) onChanged();
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Every non-system edge the root has, BEFORE the filter. The "nothing
  // connects to this yet" branch below must test the graph, not the current
  // view — a viewer who filtered everything out has not emptied the graph.
  const rootEdges = (edges || []).filter((e) => !e.is_system);

  // The filter chips describe every relation the tree currently knows about —
  // the root's edges plus anything opened — so a chip appears as soon as a
  // relation becomes reachable rather than only if the root happens to have one.
  const relOptions = useMemo(() => {
    const all = [...(edges || [])];
    for (const list of Object.values(cache)) all.push(...(list || []));
    return relationOptions(all, rootId);
  }, [edges, cache, rootId]);

  // The viewset-shaped control bar, now literally the shared component
  // (ArrangeBar, extracted 2026-07-31): a ranking rule, then the relation
  // filter. Rendered ONCE above the tree — see the state comment.
  const controls = (
    <ArrangeBar
      sorts={SORTS}
      sort={sort}
      onSort={setSort}
      filterAria="which relations to show"
      filterItems={relOptions.map(({ relation, count }) => ({
        key: relation,
        label: relation.replace(/_/g, ' '),
        count,
        on: !hiddenRelations.has(relation),
        color: relationColor(relation),
      }))}
      onToggleFilter={toggleRelation}
    />
  );

  // An object with no edges STILL renders the candidates group — that is the
  // case where the on-ramp matters most, and the pre-tree copy ("add a relation
  // under Details") pointed at a door that is no longer the shortest one.
  // Same-article siblings render ABOVE cross-page candidates: they are the
  // higher-yield group by a wide margin (531 unconnected near-duplicate pairs
  // against 7, measured 2026-07-30), and they are the ones a reader working
  // through one article can judge without leaving it.
  const candidateGroups = (
    <>
      {renderCandidateGroup({
        key: 'siblings',
        list: siblingCandidates,
        label: 'from this article, not yet connected',
        blurb:
          'Other claims pulled from the same source, with no edge to this one yet.',
      })}
      {renderCandidateGroup({
        key: 'similar',
        list: candidates,
        label: 'not yet connected',
        blurb: 'Found by similarity, not by any edge — nothing here is in the graph yet.',
      })}
    </>
  );

  const anyCandidates = (candidates?.length || 0) + (siblingCandidates?.length || 0);

  if (!rootEdges.length) {
    return (
      <div className="wvt">
        <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-ui)', marginBottom: 0 }}>
          Nothing connects to this yet{anyCandidates ? ' — but these look related:' : '.'}
        </p>
        {candidateGroups}
      </div>
    );
  }

  return (
    <div className="wvt">
      <h2 style={{ fontSize: 'var(--wv-fs-xl)', marginBottom: 'var(--wv-sp-1)' }}>
        <Explain concept="edge-sentence">Connections</Explain>
      </h2>
      {controls}
      {renderLevel(rootId, 'r', 0)}
      {candidateGroups}
    </div>
  );
}
